import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tag, MapPin } from "lucide-react";
import { fmt } from "@/lib/format";

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [method, setMethod] = useState("paystack");

  const load = () => api.get("/marketplace").then(({ data }) => setList(data));

  useEffect(() => {
    load();
  }, []);

  const buy = async (brickId, resalePrice) => {
    if (!user) return (window.location.href = "/login");
    if (user.kyc_status !== "approved") {
      toast.error("Complete KYC verification to buy bricks");
      return window.location.assign("/kyc");
    }
    if (method === "bank") {
      navigate(`/bank-transfer?brick_id=${brickId}&amount=${resalePrice}`);
      return;
    }
    setBusy(brickId);
    try {
      const endpoint = method === "paystack" ? "/paystack/marketplace/buy" : "/marketplace/buy";
      const { data } = await api.post(endpoint, {
        brick_id: brickId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Secondary market</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-2">
        Marketplace
      </h1>
      <p className="text-gray-400 mb-8 max-w-xl">
        Buy bricks directly from other investors. Every brick remains a unique, verifiable token.
      </p>

      {user && user.kyc_status === "approved" && list.length > 0 && (
        <div className="mb-6 bp-surface rounded-2xl p-4 flex flex-wrap items-center gap-3">
          <div className="overline text-[10px]">Pay with</div>
          <div className="flex gap-2">
            {[
              { v: "paystack", label: "Paystack" },
              { v: "stripe", label: "Stripe" },
              { v: "bank", label: "Bank Transfer" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setMethod(o.v)}
                data-testid={`market-pay-${o.v}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                  method === o.v
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/10 text-gray-400 hover:text-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <div className="bp-surface rounded-2xl p-12 text-center text-gray-500">
          No bricks listed right now. Check back soon.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((b) => (
            <div key={b.id} className="bp-card rounded-2xl overflow-hidden" data-testid={`market-item-${b.id}`}>
              {b.property && (
                <div className="aspect-video relative">
                  <img src={b.property.image_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050914] to-transparent" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <MapPin className="w-3 h-3" /> {b.property?.location}
                </div>
                <div className="font-heading text-lg text-white mt-1">{b.property?.name}</div>
                <div className="font-mono text-xs text-[#D4AF37] mt-2">{b.token_id}</div>

                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="overline text-[10px]">Resale price</div>
                    <div className="font-heading text-2xl text-[#D4AF37] mt-1">
                      {fmt(b.resale_price, { decimals: true })}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    data-testid={`buy-market-${b.id}`}
                    className="btn-gold rounded-full h-9 text-xs px-5"
                    disabled={busy === b.id || b.owner_id === user?.id}
                    onClick={() => buy(b.id)}
                  >
                    {b.owner_id === user?.id ? "Yours" : busy === b.id ? "..." : <><Tag className="w-3 h-3 mr-1" /> Buy</>}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
